import React from "react";
import moment from "moment";
import Select from "react-select";
import { Modal, Spinner } from "reactstrap";

const AddStudentModal = ({ show, loading, formData1, setFormData1, allStudents, compStudents, timeSlots, batchStudentCount, onSubmit, onClose }) => {
  if (!show) return null;
  
  const studentOptions = !formData1.bookingType ? [] : formData1.bookingType === "Comp" ? compStudents : allStudents;
  const now = moment();
  const tomorrow = moment().add(1, "day");
  const tomorrowDay = tomorrow.day();
  const isTomorrowWeekend = tomorrowDay === 0 || tomorrowDay === 6;
  const currentMinutes = now.hours() * 60 + now.minutes();
  const weekdayLimit = 19 * 60;
  const weekendLimit = 15 * 60;
  const canShowTomorrow = isTomorrowWeekend
    ? currentMinutes >= weekendLimit
    : currentMinutes >= weekdayLimit;
  const classDateOptions = [
    {
      value: moment().format("YYYY-MM-DD"),
      label:
        `${moment().format("dddd")} - Today (${moment().format("DD-MM-YYYY")})`,
    },
  ];

  if (canShowTomorrow) {
    classDateOptions.push({
      value: tomorrow.format("YYYY-MM-DD"),
      label:
        `${tomorrow.format("dddd")} - Tomorrow (${tomorrow.format("DD-MM-YYYY")})`,
    });
  }

  return (
    <Modal isOpen={show} toggle={onClose} backdrop="static" keyboard={false}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="modal-header">
          <h5 className="modal-title">
            Swap/Add Student
          </h5>

          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          />
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="text-center my-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label">
                  Booking Type*
                </label>

                <Select
                  placeholder="Select Booking Type"
                  options={[
                    { value: "Fixed", label: "Fixed" },
                    { value: "Comp", label: "Comp" },
                  ]}
                  value={
                    formData1.bookingType
                      ? {
                        value: formData1.bookingType,
                        label: formData1.bookingType,
                      }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setFormData1({
                      ...formData1,
                      bookingType: selectedOption?.value || "",
                      studentId: "",
                    })
                  }
                  required
                  isClearable
                  classNamePrefix="select2-selection"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Class Date*
                </label>

                <Select
                  placeholder="Select Class Date"
                  options={classDateOptions}
                  value={
                    formData1.classDate
                      ? {
                        value: formData1.classDate,
                        label:
                          moment(formData1.classDate).isSame(moment(), "day")
                            ? `${moment(formData1.classDate).format("dddd")} - Today (${moment(formData1.classDate).format("DD-MM-YYYY")})`
                            : `${moment(formData1.classDate).format("dddd")} - Tomorrow (${moment(formData1.classDate).format("DD-MM-YYYY")})`,
                      }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setFormData1({
                      ...formData1,
                      classDate: selectedOption?.value || "",
                    })
                  }
                  required
                  isClearable
                  classNamePrefix="select2-selection"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Student*
                </label>
                <Select
                  placeholder={
                    formData1.bookingType
                      ? "Select Student"
                      : "Select Booking Type First"
                  }
                  options={studentOptions.map((student) => ({
                    value: student.Id,
                    label: student.Student_Name,
                  }))}
                  value={
                    studentOptions
                      .map((student) => ({
                        value: student.Id,
                        label: student.Student_Name,
                      }))
                      .find(
                        (option) =>
                          option.value === formData1.studentId
                      ) || null
                  }
                  onChange={(selectedOption) =>
                    setFormData1({
                      ...formData1,
                      studentId: selectedOption?.value || "",
                    })
                  }
                  required
                  isClearable
                  classNamePrefix="select2-selection"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Batch Time*
                </label>
                <Select
                  placeholder="Select Batch"
                  options={timeSlots.map((slot) => {
                    const currentCount = batchStudentCount?.[slot.Batch_Time] || 0;
                    const isFull = currentCount >= slot.Max_Students;
                    const isSelected = formData1.batchTime === slot.Batch_Time;
                    const isClosedClass = Number(slot.IsClassCancelled) === 1;

                    const now = moment();
                    const batchStartTime = slot.Batch_Time.split("-")[0].trim();
                    const batchMoment = moment(
                      `${formData1.classDate} ${batchStartTime}`,
                      ["YYYY-MM-DD h:mm A", "YYYY-MM-DD h A"]
                    );
                    const disableBefore = batchMoment.clone().subtract(30, "minutes");
                    const isToday = moment(formData1.classDate).isSame(moment(), "day");
                    const isPastHalfHour = isToday && now.isSameOrAfter(disableBefore);
                    const isDisabled = isClosedClass || ((isFull || isPastHalfHour) && !isSelected);

                    return {
                      value: slot.Batch_Time,
                      label:
                        `${slot.Batch_Time} (${currentCount}/${slot.Max_Students})` +
                        `${isClosedClass  && !isSelected ? " - Closed Class" : ""}` +
                        `${isFull && !isSelected ? " - Full" : ""}` +
                        `${isPastHalfHour && !isSelected ? " - Closed" : ""}`,
                      isDisabled,
                    };
                  })}
                  value={
                    timeSlots
                      .map((slot) => ({
                        value: slot.Batch_Time,
                        label: slot.Batch_Time,
                      }))
                      .find(
                        (option) =>
                          option.value === formData1.batchTime
                      ) || null
                  }
                  onChange={(selectedOption) =>
                    setFormData1({
                      ...formData1,
                      batchTime: selectedOption?.value || "",
                    })
                  }
                  required
                  isClearable
                  classNamePrefix="select2-selection"
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddStudentModal;